/**
 * Footer Component
 *
 * Persistent footer component that appears at the bottom of every page
 * in the Presidio CC Tools application. Provides quick access to bug reporting,
 * documentation, and application information.
 */

import React, { useState } from 'react';
import {
  Box,
  Container,
  Group,
  Text,
  Button,
  Divider,
  Stack,
  Badge,
  Tooltip,
  ActionIcon
} from '@mantine/core';
import {
  IconBug,
  IconBook,
  IconBrandGithub,
  IconMail,
  IconInfoCircle
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { usePlatform } from '../shared/contexts/PlatformContext';
import BugReportModal from './BugReportModal';

/**
 * Footer Component
 */
const Footer = ({ platformColor = 'blue', compact = false }) => {
  const { currentPlatform } = usePlatform();
  const [bugReportOpened, setBugReportOpened] = useState(false);

  /**
   * Handle bug report success
   */
  const handleBugReportSuccess = (result) => {
    notifications.show({
      title: 'Report Submitted',
      message: `Your report (ID: ${result.reportId.substring(0, 8)}) has been submitted successfully.`,
      color: 'green',
      icon: <IconBug size={16} />
    });
  };

  /**
   * Render compact footer for embedded views
   */
  if (compact) {
    return (
      <>
        <Box
          component="footer"
          style={{
            borderTop: '1px solid var(--mantine-color-gray-3)',
            backgroundColor: 'var(--mantine-color-gray-0)',
            padding: '8px 16px'
          }}
        >
          <Group justify="space-between" gap="xs">
            <Text size="xs" c="dimmed">
              Presidio CC Tools © {new Date().getFullYear()}
            </Text>
            <Button
              size="xs"
              variant="subtle"
              leftSection={<IconBug size={14} />}
              onClick={() => setBugReportOpened(true)}
            >
              Report Issue
            </Button>
          </Group>
        </Box>

        <BugReportModal
          opened={bugReportOpened}
          onClose={() => setBugReportOpened(false)}
          onSuccess={handleBugReportSuccess}
        />
      </>
    );
  }

  /**
   * Render full footer
   */
  return (
    <>
      <Box
        component="footer"
        style={{
          borderTop: '2px solid var(--mantine-color-gray-3)',
          backgroundColor: 'var(--mantine-color-gray-0)',
          marginTop: 'auto',
          padding: '24px 0'
        }}
      >
        <Container size="xl">
          <Stack gap="md">
            {/* Main Footer Content */}
            <Group justify="space-between" align="flex-start">
              {/* Left Section - Application Info */}
              <Stack gap="xs" style={{ flex: 1 }}>
                <Group gap="xs">
                  <Text size="sm" fw={600}>
                    Presidio Contact Center Tools
                  </Text>
                  <Badge size="xs" variant="dot" color={platformColor}>
                    v2.0
                  </Badge>
                </Group>
                <Text size="xs" c="dimmed" style={{ maxWidth: 400 }}>
                  Unified toolkit for AWS Connect, Cisco WxCC, Five9 contact center platforms, and SA Tools.
                  Built by Presidio Network Solutions.
                </Text>
                <Group gap="xs" mt="xs">
                  <Badge size="xs" color="orange" variant="light">AWS Connect</Badge>
                  <Badge size="xs" color="blue" variant="light">Cisco WxCC</Badge>
                  <Badge size="xs" color="green" variant="light">Five9</Badge>
                  <Badge size="xs" color="purple" variant="light">SA Tools</Badge>
                </Group>
              </Stack>

              {/* Center Section - Quick Actions */}
              <Stack gap="xs" style={{ flex: 1 }}>
                <Text size="xs" fw={600} c="dimmed">
                  Quick Actions
                </Text>
                <Group gap="xs">
                  <Button
                    size="sm"
                    variant="light"
                    color={platformColor}
                    leftSection={<IconBug size={16} />}
                    onClick={() => setBugReportOpened(true)}
                  >
                    Report Issue
                  </Button>
                  <Tooltip label="Coming soon">
                    <Button
                      size="sm"
                      variant="subtle"
                      leftSection={<IconBook size={16} />}
                      disabled
                    >
                      Documentation
                    </Button>
                  </Tooltip>
                </Group>
              </Stack>

              {/* Right Section - Support Links */}
              <Stack gap="xs" style={{ flex: 1 }}>
                <Text size="xs" fw={600} c="dimmed">
                  Support & Resources
                </Text>
                <Stack gap="xs">
                  {currentPlatform === 'satools' && (
                    <Group gap="xs">
                      <IconMail size={14} />
                      <Text size="xs" c="dimmed">
                        pdave@presidio.com
                      </Text>
                    </Group>
                  )}
                  <Group gap="xs">
                    <IconMail size={14} />
                    <Text size="xs" c="dimmed">
                      pbartholomai@presidio.com
                    </Text>
                  </Group>
                  <Group gap="xs">
                    <IconMail size={14} />
                    <Text size="xs" c="dimmed">
                      cthomas@presidio.com
                    </Text>
                  </Group>
                  <Group gap="xs" mt="xs">
                    <Tooltip label="View on GitHub">
                      <ActionIcon
                        variant="subtle"
                        size="sm"
                        component="a"
                        href="https://github.com/presidio"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <IconBrandGithub size={16} />
                      </ActionIcon>
                    </Tooltip>
                    <Tooltip label="About">
                      <ActionIcon variant="subtle" size="sm">
                        <IconInfoCircle size={16} />
                      </ActionIcon>
                    </Tooltip>
                  </Group>
                </Stack>
              </Stack>
            </Group>

            <Divider />

            {/* Bottom Footer Bar */}
            <Group justify="space-between">
              <Text size="xs" c="dimmed">
                © {new Date().getFullYear()} Presidio Network Solutions. All rights reserved.
              </Text>
              <Group gap="xs">
                <Badge size="xs" variant="dot" color="green">
                  Authenticated
                </Badge>
                <Text size="xs" c="dimmed">
                  Secure Session Active
                </Text>
              </Group>
            </Group>
          </Stack>
        </Container>
      </Box>

      {/* Bug Report Modal */}
      <BugReportModal
        opened={bugReportOpened}
        onClose={() => setBugReportOpened(false)}
        onSuccess={handleBugReportSuccess}
      />
    </>
  );
};

/**
 * Minimal footer variant for authentication pages
 */
export const MinimalFooter = () => {
  return (
    <Box
      component="footer"
      style={{
        borderTop: '1px solid var(--mantine-color-gray-3)',
        backgroundColor: 'var(--mantine-color-gray-0)',
        padding: '16px',
        textAlign: 'center'
      }}
    >
      <Text size="xs" c="dimmed">
        © {new Date().getFullYear()} Presidio Network Solutions • Contact Center Tools
      </Text>
    </Box>
  );
};

export default Footer;
